import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { useTranslation } from "react-i18next";
import { AppShell } from "@/components/layout/app-shell";
import { LoadingState } from "@/components/features/states";
import { LandingView } from "@/components/views/landing";
import { AuthView } from "@/components/views/auth";
import { AppDashboard } from "@/components/views/app-dashboard";
import { JobsListView } from "@/components/views/jobs-list";
import { JobCreateView } from "@/components/views/job-create";
import { JobDetailView } from "@/components/views/job-detail";
import { ChatView } from "@/components/views/chat";
import { TrustView } from "@/components/views/trust";
import { NotificationsView } from "@/components/views/notifications";
import { ProfileView } from "@/components/views/profile";
import { AdminDashboard } from "@/components/views/admin-dashboard";
import { AdminVerifications } from "@/components/views/admin-verifications";
import { AdminDisputes } from "@/components/views/admin-disputes";
import { AdminJobs } from "@/components/views/admin-jobs";
import { AdminUsers } from "@/components/views/admin-users";
import { AdminAnalytics } from "@/components/views/admin-analytics";
import { AdminAds } from "@/components/views/admin-ads";
import { AdminCommissionRules } from "@/components/views/admin-commission-rules";
import { AdminFraudFlags } from "@/components/views/admin-fraud-flags";
import { AdminServices } from "@/components/views/admin-services";
import { SubscriptionsView } from "@/components/views/subscriptions";
import { MobileMoneyView } from "@/components/views/mobile-money";
import { StoresListView } from "@/components/views/stores-list";
import { StoreDetailView } from "@/components/views/store-detail";
import { MerchantDashboardView } from "@/components/views/merchant-dashboard";
import { CourierEarningsView } from "@/components/views/courier-earnings";
import { AdminStoresView } from "@/components/views/admin-stores";
import { RoleApplicationView } from "@/components/views/role-application";
import { WalletView } from "@/components/views/wallet";
import { AdminRoleApplicationsView } from "@/components/views/admin-role-applications";
import { TrackView } from "@/components/views/track";
import { WaterOrderView } from "@/components/views/water-order";
import { PickupDeliverView } from "@/components/views/pickup-deliver";
import { SettingsView } from "@/components/views/settings";
import {
  PrivacyPolicyView,
  TermsTrustView,
  DataProtectionView,
  SupportView,
} from "@/components/views/legal";
import { TenantUsersView, TenantScopeProvider } from "@/components/views/admin-tenants";
import { EmailTemplatePreview } from "@/components/views/admin-email-templates";
import { useEffect } from "react";
import { useSeo } from "@/lib/seo";

export function App() {
  // Subscribe to only the slices we need — using `useAuthStore()` with no
  // selector would re-render the whole App tree on EVERY auth state change
  // (including unrelated `loading` ticks), which cascades into every view.
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const route = useUiStore((s) => s.route);
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const seg = route.path.split("/").filter(Boolean);

  // Update <head> (title, meta, canonical, JSON-LD) on every route change
  // so AI bots, LLMs and search engines see accurate per-route metadata.
  useSeo();

  useEffect(() => {
    if (!initialized) return;
    if (!user) {
      const isPublic =
        !seg[0] ||
        ["login", "register", "verify", "legal"].includes(seg[0]);
      if (!isPublic) navigate("");
    } else {
      if (seg[0] === "admin" && !user.isAdmin) {
        navigate("app");
      } else if (seg[0] === "merchant" && user.activeRole !== "merchant") {
        // Non-merchant user hitting #/merchant — redirect to app dashboard
        // instead of falling through to the customer AppShell (which would
        // silently render AppDashboard on the merchant route).
        navigate("app");
      } else if (seg[0] === "role-select") {
        // allow
      } else if (!seg[0] || ["login", "register", "verify"].includes(seg[0])) {
        navigate(user.isAdmin ? "admin" : user.activeRole === "merchant" ? "merchant" : "app");
      } else if (user.isAdmin && seg[0] === "app" && !seg[1]) {
        // Admin on bare #/app (the customer landing) — redirect to #/admin
        // so the AdminDashboard renders instead of AppDashboard.
        navigate("admin");
      }
    }
  }, [initialized, user, route.path, navigate]);

  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label={t("app.loading")} />
      </div>
    );
  }

  if (!user) {
    if (seg[0] === "register" || seg[0] === "login" || seg[0] === "verify") {
      return <AuthView mode={seg[0]} />;
    }
    if (seg[0] === "legal") {
      return <LegalRouter page={seg[1]} />;
    }
    return <LandingView />;
  }

  if (seg[0] === "legal") {
    return <LegalRouter page={seg[1]} />;
  }

  if (seg[0] === "role-select") {
    return <AuthView mode="role-select" />;
  }

  if (user.isAdmin && seg[0] === "admin") {
    const view = (() => {
      switch (seg[1]) {
        case "verifications":
          return <AdminVerifications />;
        case "disputes":
          return <AdminDisputes />;
        case "jobs":
          return <AdminJobs />;
        case "users":
          return <AdminUsers />;
        case "analytics":
          return <AdminAnalytics />;
        case "ads":
          return <AdminAds />;
        case "commission-rules":
          return <AdminCommissionRules />;
        case "fraud-flags":
          return <AdminFraudFlags />;
        case "services":
          return <AdminServices />;
        case "stores":
          return <AdminStoresView />;
        case "role-applications":
          return <AdminRoleApplicationsView />;
        case "tenants":
          return <TenantUsersView />;
        case "email-templates":
          return <EmailTemplatePreview />;
        default:
          return <AdminDashboard />;
      }
    })();
    return (
      <TenantScopeProvider>
        <AppShell>{view}</AppShell>
      </TenantScopeProvider>
    );
  }

  if (user.activeRole === "merchant" && seg[0] === "merchant") {
    return (
      <AppShell>
        <MerchantDashboardView />
      </AppShell>
    );
  }

  const view = (() => {
    switch (seg[1]) {
      case "jobs":
        if (seg[2] === "new") return <JobCreateView />;
        if (seg[2]) return <JobDetailView jobId={seg[2]} />;
        return <JobsListView />;
      case "chat":
        if (seg[2]) return <ChatView jobId={seg[2]} />;
        return <JobsListView />;
      case "stores":
        if (seg[2]) return <StoreDetailView storeId={seg[2]} />;
        return <StoresListView />;
      case "trust":
        return <TrustView />;
      case "notifications":
        return <NotificationsView />;
      case "profile":
        return <ProfileView />;
      case "subscriptions":
        return <SubscriptionsView />;
      case "mobile-money":
        return <MobileMoneyView />;
      case "earnings":
        return <CourierEarningsView />;
      case "merchant":
        return <MerchantDashboardView />;
      case "role-application":
        return <RoleApplicationView />;
      case "wallet":
        return <WalletView />;
      case "track":
        return <TrackView />;
      case "water-order":
        return <WaterOrderView />;
      case "pickup-deliver":
        return <PickupDeliverView />;
      case "settings":
        return <SettingsView />;
      case "help":
        return <SupportView />;
      default:
        return <AppDashboard />;
    }
  })();
  return <AppShell>{view}</AppShell>;
}

function LegalRouter({ page }: { page?: string }) {
  switch (page) {
    case "terms":
      return <TermsTrustView />;
    case "data-protection":
      return <DataProtectionView />;
    case "support":
      return <SupportView />;
    case "privacy":
    default:
      return <PrivacyPolicyView />;
  }
}
